import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import JobCard from "../../components/Jobs/JobCard";
import JobFilters from "../../components/Jobs/JobFilters";
import SavedJobs from "../../components/Jobs/SavedJobs";
import * as jobService from "../../services/jobService";
import * as resumeService from "../../services/resumeService";
import { recommendJobs } from "../../utils/jobRecommendations";
import { toast } from "react-toastify";

export default function Jobs() {
  const [searchParams, setSearchParams] = useState({
    keyword: "",
    location: "",
    experienceLevel: [],
    employmentType: [],
    locationType: "",
    salaryMin: 0,
    salaryMax: 200000,
    datePosted: "any",
    page: 1,
    limit: 12,
  });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalJobs, setTotalJobs] = useState(0);
  const [view, setView] = useState("list");
  const [sort, setSort] = useState("relevance");
  const [recommended, setRecommended] = useState([]);
  const [tab, setTab] = useState("all"); // 'all' | 'saved'

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const r = await resumeService.getAllResumes(1, 1);
        const rec = await jobService.searchJobs({ page: 1, limit: 20 });
        const top = recommendJobs(rec.jobs || [], (r.resumes || [])[0]);
        setRecommended(top.slice(0, 8));
      } catch {}
    })();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const result = await jobService.searchJobs({
        keyword: searchParams.keyword,
        location: searchParams.location,
        experienceLevel: searchParams.experienceLevel[0] || "",
        employmentType: searchParams.employmentType[0] || "",
        locationType: searchParams.locationType || "",
        page: searchParams.page,
        limit: searchParams.limit,
      });
      setJobs(result.jobs || []);
      setTotalJobs(result.total || 0);
    } catch (error) {
      toast.error("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };

  const sortedJobs = useMemo(() => {
    const arr = [...jobs];
    if (sort === "date") {
      arr.sort(
        (a, b) =>
          new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime()
      );
    } else if (sort === "match") {
      arr.sort((a, b) => (b.matchPercentage || 0) - (a.matchPercentage || 0));
    }
    return arr;
  }, [jobs, sort]);

  function onSave(job) {
    const raw = localStorage.getItem("saved_jobs");
    const list = raw ? JSON.parse(raw) : [];
    if (!list.find((j) => j._id === job._id)) {
      list.push(job);
      localStorage.setItem("saved_jobs", JSON.stringify(list));
      toast.success("Job saved");
    }
  }

  function onSearchClick() {
    setSearchParams((p) => ({ ...p, page: 1 }));
  }

  const totalPages = Math.max(1, Math.ceil(totalJobs / searchParams.limit));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Job Search</h1>
            <p className="text-sm sm:text-base text-gray-600">
              Find roles that match your skills
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <button
              className={`px-2 py-1 text-sm rounded ${
                view === "list" ? "bg-gray-200" : ""
              }`}
              onClick={() => setView("list")}
            >
              List
            </button>
            <button
              className={`px-2 py-1 text-sm rounded ${
                view === "grid" ? "bg-gray-200" : ""
              }`}
              onClick={() => setView("grid")}
            >
              Grid
            </button>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="relevance">Relevance</option>
              <option value="date">Date</option>
              <option value="match">Match</option>
            </select>
          </div>
        </header>

        {/* Recommended */}
        {recommended.length > 0 && (
          <Card title="Jobs for You">
            <div className="overflow-x-auto">
              <div className="flex gap-3 min-w-max">
                {recommended.map((j) => (
                  <div key={j._id} className="min-w-[260px]">
                    <JobCard
                      job={j}
                      view="grid"
                      showMatchScore
                      onSave={(e) => {
                        e?.preventDefault?.();
                        onSave(j);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Search bar */}
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              className="border rounded px-3 py-2 sm:col-span-1"
              placeholder="Keyword"
              value={searchParams.keyword}
              onChange={(e) =>
                setSearchParams((p) => ({ ...p, keyword: e.target.value }))
              }
            />
            <input
              className="border rounded px-3 py-2 sm:col-span-1"
              placeholder="Location"
              value={searchParams.location}
              onChange={(e) =>
                setSearchParams((p) => ({ ...p, location: e.target.value }))
              }
            />
            <Button onClick={onSearchClick} className="w-full sm:w-auto">
              Search
            </Button>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="sticky top-4">
              <JobFilters
                filters={searchParams}
                onFilterChange={(f) =>
                  setSearchParams((p) => ({ ...p, ...f, page: 1 }))
                }
                onClear={() =>
                  setSearchParams((p) => ({
                    ...p,
                    experienceLevel: [],
                    employmentType: [],
                    locationType: "",
                    salaryMin: 0,
                    salaryMax: 200000,
                    datePosted: "any",
                    page: 1,
                  }))
                }
              />
              <div className="mt-6">
                <div className="flex items-center gap-2 border-b overflow-x-auto">
                  <button
                    className={`px-3 py-2 whitespace-nowrap ${
                      tab === "all"
                        ? "border-b-2 border-primary-600 text-primary-700"
                        : "text-gray-600"
                    }`}
                    onClick={() => setTab("all")}
                  >
                    All Jobs
                  </button>
                  <button
                    className={`px-3 py-2 whitespace-nowrap ${
                      tab === "saved"
                        ? "border-b-2 border-primary-600 text-primary-700"
                        : "text-gray-600"
                    }`}
                    onClick={() => setTab("saved")}
                  >
                    Saved
                  </button>
                </div>
                {tab === "saved" && <SavedJobs />}
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <Card title={`Results (${totalJobs})`}>
              {loading ? (
                <LoadingSkeleton />
              ) : !sortedJobs.length ? (
                <p className="text-sm text-gray-500">No jobs found.</p>
              ) : (
                <div
                  className={`grid ${
                    view === "grid"
                      ? "grid-cols-1 sm:grid-cols-2 gap-3"
                      : "grid-cols-1 gap-3"
                  }`}
                >
                  {sortedJobs.map((j) => (
                    <JobCard
                      key={j._id}
                      job={j}
                      view={view}
                      onSave={(e) => {
                        e?.preventDefault?.();
                        onSave(j);
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div>
                  <Button
                    variant="ghost"
                    disabled={searchParams.page <= 1}
                    onClick={() =>
                      setSearchParams((p) => ({
                        ...p,
                        page: Math.max(1, p.page - 1),
                      }))
                    }
                  >
                    ← Previous
                  </Button>
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  Page {searchParams.page} of {totalPages}
                </span>
                <div>
                  <Button
                    variant="ghost"
                    disabled={searchParams.page >= totalPages}
                    onClick={() =>
                      setSearchParams((p) => ({
                        ...p,
                        page: Math.min(totalPages, p.page + 1),
                      }))
                    }
                  >
                    Next →
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="border rounded-lg p-4 animate-pulse bg-white">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="mt-2 h-3 bg-gray-200 rounded w-1/2" />
          <div className="mt-2 h-3 bg-gray-200 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}
